import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';
import { DataTableSortStatus } from 'mantine-datatable';
import { useEffect, useState } from 'react';
import { api, type ListResponse } from 'src/common/utils/api';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { BaseRecord } from '../types';
import { TableData } from './useTable';

export const useServerInfiniteTable = <T extends BaseRecord>(
  resource: string,
  pageSize: number,
  filters?: Record<string, unknown>,
  initialSort?: DataTableSortStatus<T>,
  enabled: boolean = true,
): TableData<T> => {
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<T> | undefined>(initialSort);

  const resourceSegments = resource.split('/');
  const sortParams = sortStatus
    ? { sortBy: sortStatus.columnAccessor, sortDir: sortStatus.direction }
    : {};

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    failureReason,
  } = useInfiniteQuery<
    ListResponse<T>,
    Error,
    InfiniteData<ListResponse<T>>,
    readonly unknown[],
    number
  >({
    queryKey: [...resourceSegments, { size: pageSize, ...filters, ...sortParams }],
    queryFn: ({ pageParam }) =>
      api.getPaginated<T>(resource, { page: pageParam, size: pageSize, ...filters, ...sortParams }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((sum, page) => sum + page.items.length, 0);
      const hasMore = lastPage.items.length > 0 && totalFetched < lastPage.total;
      return hasMore ? allPages.length + 1 : undefined;
    },
    enabled,
  });

  useEffect(() => {
    if (isError || isFetchNextPageError) {
      notifyAndLogError(
        'Failed to load data',
        'An error occurred while loading table data. Please try again later.',
        error ?? failureReason,
        { resource, pageSize },
      );
    }
  }, [isError, isFetchNextPageError, error, failureReason, resource, pageSize]);

  const processedData = data?.pages.flatMap(page => page.items) ?? [];
  const processedTotalRecords = data?.pages[0]?.total ?? 0;

  return {
    data: processedData,
    isLoading,
    isFetching,
    isError,
    error,
    pagination: undefined,
    totalRecords: processedTotalRecords,
    refetch: () => {
      try {
        return refetch();
      } catch (error) {
        notifyAndLogError(
          'Refresh failed',
          'Failed to refresh table data. Please try again.',
          error,
          { resource },
        );
        throw error;
      }
    },
    sortStatus,
    onSortStatusChange: setSortStatus,
    hasNextPage: hasNextPage && !isFetchNextPageError,
    fetchNextPage,
    isFetchingNextPage,
  };
};
